/**
 * Extension Communication
 *
 * Handles communication with the Numeo Chrome extension for authentication flow.
 * Note: Data fetching no longer requires the extension - it's done directly via API.
 * The extension is only needed for opening auth tabs (login flow) to capture tokens/cookies.
 */

import { EXTENSION_ID, AUTH_LOGIN_MESSAGE_TYPES, PROVIDER_CONFIGS } from './constants'
import { FactoringProvider } from './types'
import { getAuthToken } from 'setup/SetupAxios'

/**
 * Check if Chrome extension messaging is available
 */
export const isExtensionAvailable = (): boolean => {
    return typeof chrome !== 'undefined' && !!chrome.runtime && !!chrome.runtime.sendMessage
}

/**
 * A file the extension publishes to any page (`web_accessible_resources` in its manifest).
 * Small and static — it exists only to be fetched as a presence signal.
 */
const EXTENSION_PRESENCE_RESOURCE = 'icons/image.png'

/**
 * Whether the Numeo extension is installed AND enabled in this browser.
 *
 * Reads a static file straight out of the extension package, so it never involves the MV3 service
 * worker. That is the whole point: `chrome.runtime.lastError` fires both when the extension is
 * absent and when an installed worker was merely asleep, and it gives callers no way to tell those
 * apart — this does, with no timeout and nothing to keep in sync. Chrome stops serving a disabled
 * extension's resources, so disabled reads as absent, which is what callers mean by "can I use it".
 */
export const isExtensionInstalled = async (): Promise<boolean> => {
    try {
        const response = await fetch(`chrome-extension://${EXTENSION_ID}/${EXTENSION_PRESENCE_RESOURCE}`)
        return response.ok
    } catch {
        // Not installed, or not a Chromium browser at all — either way it cannot be used here.
        return false
    }
}

interface OpenAuthTabResult {
    success: boolean
    tabId?: number
    error?: string
    /**
     * Set when the extension itself is what failed, so a caller can offer install guidance instead
     * of a generic error. Optional and additive: existing callers keep reading `error` unchanged.
     */
    reason?: 'extension_unavailable'
}

const EXTENSION_REQUIRED_ERROR = 'Numeo Chrome Extension is required for factoring authentication. Please install and enable the extension.'
const ENGLAND_EXTENSION_REQUIRED_ERROR = 'Numeo Chrome Extension is required to read England Logistics. Please install and enable the extension.'

/**
 * Open auth tab for a factoring provider via extension
 * Returns error string if extension is not available or communication fails
 */
export const openFactoringAuthTab = (provider: FactoringProvider, tokenPurpose?: 'accounting' | 'rating'): Promise<OpenAuthTabResult> => {
    return new Promise((resolve) => {
        const loginUrl = PROVIDER_CONFIGS[provider].loginUrl
        const authToken = getAuthToken()

        // If extension is not available, return error - extension is required for auth
        if (!isExtensionAvailable()) {
            resolve({ success: false, reason: 'extension_unavailable', error: EXTENSION_REQUIRED_ERROR })
            return
        }

        const message = {
            type: AUTH_LOGIN_MESSAGE_TYPES[provider],
            url: loginUrl,
            ...(tokenPurpose && { tokenPurpose }),
            // Bind the capture to this tab's active company. The extension's global Chrome-storage
            // token can still point at the base company while this admin tab uses a Multi-MC override.
            ...(provider === FactoringProvider.RTS && authToken && { authToken }),
        }

        try {
            chrome.runtime.sendMessage(EXTENSION_ID, message, (response) => {
                if (chrome.runtime.lastError) {
                    console.warn('[FactoringService] Extension error:', chrome.runtime.lastError.message)
                    resolve({ success: false, reason: 'extension_unavailable', error: EXTENSION_REQUIRED_ERROR })
                    return
                }
                if (response?.success) {
                    resolve(response)
                } else {
                    resolve({ success: false, tabId: response?.tabId, error: 'Failed to open authentication tab. Please try again.' })
                }
            })
        } catch (error) {
            console.warn('[FactoringService] Failed to send message:', error)
            resolve({ success: false, reason: 'extension_unavailable', error: EXTENSION_REQUIRED_ERROR })
        }
    })
}

/**
 * Open (or re-read) the England portal.
 *
 * Separate from `openFactoringAuthTab` because England has two modes that must behave differently:
 * connecting brings the tab forward — the dispatcher has to log in there — while a sync must stay
 * silent, since being yanked into the portal every time Sync is pressed is exactly the disruption a
 * background status refresh is supposed to avoid.
 */
export const openEnglandPortal = (options?: { focus?: boolean }): Promise<OpenAuthTabResult> => {
    return new Promise((resolve) => {
        if (!isExtensionAvailable()) {
            resolve({ success: false, reason: 'extension_unavailable', error: ENGLAND_EXTENSION_REQUIRED_ERROR })
            return
        }

        const message = {
            type: AUTH_LOGIN_MESSAGE_TYPES[FactoringProvider.ENGLAND],
            url: PROVIDER_CONFIGS[FactoringProvider.ENGLAND].loginUrl,
            tokenPurpose: 'accounting',
            focus: options?.focus !== false,
        }

        try {
            chrome.runtime.sendMessage(EXTENSION_ID, message, (response) => {
                if (chrome.runtime.lastError) {
                    console.warn('[FactoringService] Extension error:', chrome.runtime.lastError.message)
                    resolve({ success: false, reason: 'extension_unavailable', error: ENGLAND_EXTENSION_REQUIRED_ERROR })
                    return
                }
                resolve(response?.success ? response : { success: false, tabId: response?.tabId, error: 'Failed to open England. Please try again.' })
            })
        } catch (error) {
            console.warn('[FactoringService] Failed to send message:', error)
            resolve({ success: false, reason: 'extension_unavailable', error: ENGLAND_EXTENSION_REQUIRED_ERROR })
        }
    })
}
